import { useState } from 'react'
import { Botao } from '../../../components/Botao'
import { Dialogo } from '../../../components/Dialogo'
import { api } from '../../../api/client'
import { dataHora } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { Anexo } from '../../../types/gmud'

/** "1,2 MB" — o tamanho como a pessoa o lê, não em bytes. */
function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} bytes`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}

/**
 * Uma comprovação na lista.
 *
 * **O download passa pelo cliente HTTP, e não por um `<a href>`.** A rota exige o cabeçalho de
 * autorização, que um link comum não manda — ele cairia em 401 e o navegador abriria uma página de
 * erro em branco no lugar do arquivo.
 *
 * **Os oito primeiros caracteres do hash aparecem.** Não é enfeite técnico: é o que permite a quem
 * recebeu o arquivo por e-mail conferir, com `sha256sum`, que é o mesmo que está aqui — sem
 * depender da palavra do sistema. Numa evidência de processo administrativo, isso é o ponto.
 *
 * **Excluir pergunta antes, nomeando o arquivo.** O botão ficava ao lado de "Baixar", do mesmo
 * tamanho, e disparava um DELETE físico no primeiro clique. Anexo é prova documental de contrato, a
 * tabela nasce vazia, e a UAT é exatamente onde os primeiros anexos nascem — e podiam morrer no
 * clique errado, sem nada perguntar. O servidor também recusa o que não é seu; isto é a pausa antes
 * do que É seu.
 *
 * @param props o anexo e o que fazer ao excluir
 * @returns a linha
 */
export function LinhaDoAnexo({
  gmudId,
  anexo,
  aoExcluir,
  excluindo,
}: {
  gmudId: number
  anexo: Anexo
  aoExcluir: () => void
  excluindo: boolean
}) {
  const [confirmando, setConfirmando] = useState(false)
  const t = TEXTOS.anexos.confirmarExclusao

  async function baixar() {
    const resposta = await api.get(`/gmuds/${gmudId}/anexos/${anexo.id}/conteudo`, { responseType: 'blob' })
    const endereco = URL.createObjectURL(resposta.data as Blob)
    const link = document.createElement('a')
    link.href = endereco
    link.download = anexo.nomeOriginal
    link.click()
    // Sem o revoke, cada download deixa o arquivo inteiro na memória da aba até ela fechar — e
    // conferir dez comprovações de 20 MB numa sessão de fiscalização trava o navegador.
    URL.revokeObjectURL(endereco)
  }

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-900">{anexo.nomeOriginal}</p>
        <p className="mt-0.5 text-xs text-slate-500">
          {anexo.categoriaRotulo} · {tamanhoLegivel(anexo.tamanhoBytes)} · {anexo.enviadoPorNome ?? '—'} ·{' '}
          {dataHora(anexo.enviadoEm)}
        </p>
        <p className="mt-0.5 font-mono text-xs text-slate-400" title={`SHA-256: ${anexo.sha256}`}>
          {TEXTOS.anexos.impressao}: {anexo.sha256.slice(0, 8)}…
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        <Botao tamanho="pequeno" onClick={() => void baixar()}>
          {TEXTOS.anexos.baixar}
        </Botao>
        <Botao tamanho="pequeno" onClick={() => setConfirmando(true)} disabled={excluindo}>
          {TEXTOS.anexos.excluir}
        </Botao>
      </div>

      {confirmando && (
        <Dialogo
          titulo={t.titulo}
          aoFechar={() => setConfirmando(false)}
          rodape={
            <>
              <Botao onClick={() => setConfirmando(false)}>Cancelar</Botao>
              <Botao
                aparencia="perigo"
                onClick={() => {
                  setConfirmando(false)
                  aoExcluir()
                }}
              >
                {t.confirmar}
              </Botao>
            </>
          }
        >
          <p className="max-w-prose text-sm text-slate-700">{t.ajuda(anexo.nomeOriginal)}</p>
        </Dialogo>
      )}
    </li>
  )
}
